import React, { useMemo, useState } from 'react';
import { Pressable, ScrollView, StyleSheet, View } from 'react-native';

import { HoverRow } from '@/components/common/line-items-table';
import { LocalTypeahead } from '@/components/common/local-typeahead';
import { Button, Modal, Text } from '@/components/ui';
import { type OwnerResolution } from '@/services/qb-compile-client';
import { formatMoneyCents } from '@/utils/format-money';
import { type UnresolvedOwner } from '@/utils/qb-compile';

export interface UnresolvedOwnersModalProps {
	visible: boolean;
	/** Distinct owner QB "Name"s that didn't resolve to any account. */
	unresolved: UnresolvedOwner[];
	/** Accounts (existing + IIF-imported) to map an unmatched name onto. */
	accounts: Array<{ id: string; label: string; accountNumber?: string }>;
	/** Confirmed decisions, one per unmatched owner (map → account, or skip). */
	onResolve: (decisions: OwnerResolution[]) => void;
	onCancel: () => void;
}

const keyOf = (name: string): string => name.trim().toLowerCase();

/**
 * Review for owner (A/R) names the import couldn't match to an account. Each row
 * defaults to "Journal-only" but the user can OVERRIDE by mapping it onto any
 * existing/IIF-imported account — e.g. "7 - ` Whalen, Living Trust" is really
 * account 7 (Whalen) with ", Living Trust" as a description. Rows are
 * highlighted (amber = unmatched, green = mapped) and outlined on hover. This
 * gate exists because the QB bundle importer used to drop unmatched A/R silently
 * while reporting success (the West Harbor Lagoons incident).
 */
export const UnresolvedOwnersModal: React.FC<UnresolvedOwnersModalProps> = ({
	visible, unresolved, accounts, onResolve, onCancel,
}) => {
	const [decisions, setDecisions] = useState<Record<string, OwnerResolution>>(() => {
		const init: Record<string, OwnerResolution> = {};
		for (const u of unresolved) init[keyOf(u.name)] = { name: u.name, action: 'skip' };
		return init;
	});

	const setAction = (name: string, action: 'map' | 'skip'): void =>
		setDecisions((d) => ({ ...d, [keyOf(name)]: { ...d[keyOf(name)], name, action } }));
	const setMapTarget = (name: string, accountId: string | null): void =>
		setDecisions((d) => ({ ...d, [keyOf(name)]: { name, action: 'map', accountId: accountId ?? undefined } }));

	// A 'skip' is always valid; a 'map' needs a chosen account.
	const allResolved = useMemo(
		() => unresolved.every((u) => {
			const dec = decisions[keyOf(u.name)];
			return dec && (dec.action === 'skip' || !!dec.accountId);
		}),
		[unresolved, decisions],
	);
	const mappedCount = unresolved.filter((u) => decisions[keyOf(u.name)]?.action === 'map' && decisions[keyOf(u.name)]?.accountId).length;

	return (
		<Modal visible={visible} onDismiss={onCancel} contentContainerStyle={styles.modal}>
			<View style={styles.header}>
				<Text style={styles.title}>Unmatched owners</Text>
				<Text style={styles.subtitle}>
					{unresolved.length} QB owner {unresolved.length === 1 ? 'name' : 'names'} didn&rsquo;t match an account.
					Map each onto an existing or imported account (e.g. &ldquo;7 - &#96; Whalen, Living Trust&rdquo; → account 7,
					Whalen), or leave it journal-only. {mappedCount > 0 ? `${mappedCount} mapped.` : ''}
				</Text>
			</View>

			<ScrollView style={styles.body} contentContainerStyle={styles.bodyContent}>
				{unresolved.map((u) => {
					const dec = decisions[keyOf(u.name)];
					const isMap = dec?.action === 'map';
					const mapped = isMap && !!dec?.accountId;
					const k = keyOf(u.name);
					return (
						<HoverRow
							key={k}
							hoverable
							testID={`uo-row-${k}`}
							style={[styles.row, mapped ? styles.rowMapped : styles.rowUnmatched]}
						>
							<View style={styles.rowHead}>
								<Text style={styles.name}>{u.name}</Text>
								<Text style={styles.context}>
									{u.count} {u.count === 1 ? 'entry' : 'entries'} · e.g. {formatMoneyCents(u.sampleCents)} via {u.qbType}
								</Text>
							</View>
							<View style={styles.choiceRow}>
								<Pressable onPress={() => setAction(u.name, 'map')} style={[styles.chip, isMap && styles.chipActive]} testID={`uo-map-${k}`}>
									<Text style={[styles.chipText, isMap && styles.chipTextActive]}>Map to account</Text>
								</Pressable>
								<Pressable onPress={() => setAction(u.name, 'skip')} style={[styles.chip, !isMap && styles.chipActive]} testID={`uo-skip-${k}`}>
									<Text style={[styles.chipText, !isMap && styles.chipTextActive]}>Journal-only</Text>
								</Pressable>
							</View>
							{isMap ? (
								<LocalTypeahead
									items={accounts}
									toOption={(a) => ({ id: a.id, label: a.label })}
									value={dec?.accountId ?? null}
									onChange={(id) => setMapTarget(u.name, id)}
									placeholder="Search accounts (number or owner)…"
									testID={`uo-typeahead-${k}`}
								/>
							) : (
								<Text style={styles.skipHint}>Posts to the journal only — no per-owner balance.</Text>
							)}
						</HoverRow>
					);
				})}
			</ScrollView>

			<View style={styles.footer}>
				<Button mode="text" onPress={onCancel}>Cancel &amp; fix</Button>
				<Button mode="contained" disabled={!allResolved} onPress={() => onResolve(unresolved.map((u) => decisions[keyOf(u.name)]))} testID="uo-confirm">
					Continue import
				</Button>
			</View>
		</Modal>
	);
};

const styles = StyleSheet.create({
	modal: { width: '96%', maxWidth: 760, height: '88%', padding: 0 },
	header: { paddingHorizontal: 20, paddingTop: 20, paddingBottom: 12, borderBottomWidth: 1, borderBottomColor: '#eceff1' },
	title: { fontSize: 18, fontWeight: '700', color: '#b71c1c', marginBottom: 4 },
	subtitle: { fontSize: 13, color: '#78909c' },
	body: { flex: 1 },
	bodyContent: { padding: 16, gap: 12 },
	row: { gap: 8, padding: 10, borderRadius: 6, borderWidth: 1, borderColor: '#eceff1' },
	rowUnmatched: { backgroundColor: '#fff8e1', borderColor: '#ffe082' },
	rowMapped: { backgroundColor: '#e8f5e9', borderColor: '#a5d6a7' },
	rowHead: { gap: 2 },
	name: { fontSize: 15, fontWeight: '600', color: '#212121' },
	context: { fontSize: 12, color: '#78909c' },
	choiceRow: { flexDirection: 'row', gap: 8 },
	chip: { paddingVertical: 6, paddingHorizontal: 14, borderRadius: 16, borderWidth: 1, borderColor: '#b0bec5' },
	chipActive: { backgroundColor: '#e3f2fd', borderColor: '#1565c0' },
	chipText: { fontSize: 13, color: '#546e7a' },
	chipTextActive: { color: '#1565c0', fontWeight: '600' },
	skipHint: { fontSize: 12, color: '#78909c', fontStyle: 'italic' },
	footer: {
		flexDirection: 'row', justifyContent: 'flex-end', gap: 8,
		paddingHorizontal: 20, paddingVertical: 14, borderTopWidth: 1, borderTopColor: '#eceff1',
	},
});
